/**
 * Mini design system for the Design Review extension.
 *
 * Single source of truth for colors, radii, spacing, shadows, typography,
 * motion and z-index. Surfaces consume tokens two ways:
 *
 * 1. **Typed access** — `import { tokens } from '../shared/tokens'` for
 *    inline styles, computed colors, severity mappings.
 * 2. **CSS variables** — `import { TOKENS_CSS } from '../shared/tokens'`
 *    and inject the string into the surface's stylesheet. Works for both
 *    regular DOM (popup, sidebar) and shadow-DOM popovers via the
 *    `:where(:root, :host)` selector.
 *
 * A live preview of every token lives at `/design-system.html` in the repo
 * root — open it directly in the browser.
 *
 * Adding a token: extend the TS object below AND the matching declaration
 * in `TOKENS_CSS`. They must stay in sync — a small amount of duplication
 * is the price of having one file with no build step generating CSS.
 */

export const tokens = {
  color: {
    bg: '#ffffff',
    bgMuted: '#f3f4f6',
    bgSoft: '#f9fafb',
    border: '#e5e7eb',
    borderStrong: '#d1d5db',
    text: '#111827',
    textMuted: '#6b7280',
    textSubtle: '#9ca3af',
    primary: '#2e3d62',
    primaryHover: '#1f2a47',
    primaryFg: '#ffffff',
    primaryFocus: '#94a2c5',
    danger: '#ef4444',
    dangerText: '#dc2626',
    warning: '#f59e0b',
    warningText: '#b45309',
    success: '#10b981',
    successText: '#047857',
    overlayDim: 'rgba(15, 23, 42, 0.22)',
  },
  radius: {
    sm: '8px',
    md: '16px',
    lg: '24px',
    full: '9999px',
  },
  space: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    xxl: '32px',
  },
  shadow: {
    popover: '0 14px 34px rgba(15, 23, 42, 0.22)',
    chip: '0 1px 2px rgba(15, 23, 42, 0.08)',
    card: '0 4px 12px rgba(15, 23, 42, 0.08)',
  },
  font: {
    family:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    familyMono: "ui-monospace, SFMono-Regular, Menlo, monospace",
    featureSettings: "'cv11', 'ss01'",
  },
  /**
   * Text roles bundle size, weight and line-height. Each role maps to a
   * triplet of CSS variables (`--text-<role>-size|weight|line-height`) so a
   * single role change in tokens.ts ripples through all surfaces.
   */
  text: {
    heading: { size: '20px', weight: 600, lineHeight: '1.25' },
    title: { size: '15px', weight: 600, lineHeight: '1.35' },
    body: { size: '13px', weight: 400, lineHeight: '1.5' },
    bodyStrong: { size: '13px', weight: 600, lineHeight: '1.5' },
    label: { size: '12px', weight: 500, lineHeight: '1.4' },
    caption: { size: '11px', weight: 400, lineHeight: '1.4' },
    mono: { size: '11px', weight: 400, lineHeight: '1.4' },
  },
  motion: {
    fast: '80ms ease',
    base: '150ms ease',
    slow: '240ms ease',
  },
  zIndex: {
    base: 1,
    overlay: 2147483640,
    popover: 2147483647,
  },
  opacity: {
    disabled: '0.65',
    muted: '0.6',
  },
} as const;

/**
 * CSS custom properties that mirror `tokens`. Inject this string into the
 * stylesheet of any surface (page <head>, shadow-DOM <style>, etc.) and
 * reference values via `var(--color-primary)` etc. in the rules below.
 *
 * `:where(:root, :host)` keeps specificity at 0 and works in both regular
 * DOM and shadow DOM with one declaration.
 */
export const TOKENS_CSS = `
  :where(:root, :host) {
    --color-bg: ${tokens.color.bg};
    --color-bg-muted: ${tokens.color.bgMuted};
    --color-bg-soft: ${tokens.color.bgSoft};
    --color-border: ${tokens.color.border};
    --color-border-strong: ${tokens.color.borderStrong};
    --color-text: ${tokens.color.text};
    --color-text-muted: ${tokens.color.textMuted};
    --color-text-subtle: ${tokens.color.textSubtle};
    --color-primary: ${tokens.color.primary};
    --color-primary-hover: ${tokens.color.primaryHover};
    --color-primary-fg: ${tokens.color.primaryFg};
    --color-primary-focus: ${tokens.color.primaryFocus};
    --color-danger: ${tokens.color.danger};
    --color-danger-text: ${tokens.color.dangerText};
    --color-warning: ${tokens.color.warning};
    --color-warning-text: ${tokens.color.warningText};
    --color-success: ${tokens.color.success};
    --color-success-text: ${tokens.color.successText};
    --color-overlay-dim: ${tokens.color.overlayDim};

    --radius-sm: ${tokens.radius.sm};
    --radius-md: ${tokens.radius.md};
    --radius-lg: ${tokens.radius.lg};
    --radius-full: ${tokens.radius.full};

    --space-xs: ${tokens.space.xs};
    --space-sm: ${tokens.space.sm};
    --space-md: ${tokens.space.md};
    --space-lg: ${tokens.space.lg};
    --space-xl: ${tokens.space.xl};
    --space-xxl: ${tokens.space.xxl};

    --shadow-popover: ${tokens.shadow.popover};
    --shadow-chip: ${tokens.shadow.chip};
    --shadow-card: ${tokens.shadow.card};

    --font-family: ${tokens.font.family};
    --font-family-mono: ${tokens.font.familyMono};
    --font-feature-settings: ${tokens.font.featureSettings};

    --text-heading-size: ${tokens.text.heading.size};
    --text-heading-weight: ${tokens.text.heading.weight};
    --text-heading-line-height: ${tokens.text.heading.lineHeight};
    --text-title-size: ${tokens.text.title.size};
    --text-title-weight: ${tokens.text.title.weight};
    --text-title-line-height: ${tokens.text.title.lineHeight};
    --text-body-size: ${tokens.text.body.size};
    --text-body-weight: ${tokens.text.body.weight};
    --text-body-line-height: ${tokens.text.body.lineHeight};
    --text-body-strong-size: ${tokens.text.bodyStrong.size};
    --text-body-strong-weight: ${tokens.text.bodyStrong.weight};
    --text-body-strong-line-height: ${tokens.text.bodyStrong.lineHeight};
    --text-label-size: ${tokens.text.label.size};
    --text-label-weight: ${tokens.text.label.weight};
    --text-label-line-height: ${tokens.text.label.lineHeight};
    --text-caption-size: ${tokens.text.caption.size};
    --text-caption-weight: ${tokens.text.caption.weight};
    --text-caption-line-height: ${tokens.text.caption.lineHeight};
    --text-mono-size: ${tokens.text.mono.size};
    --text-mono-weight: ${tokens.text.mono.weight};
    --text-mono-line-height: ${tokens.text.mono.lineHeight};

    --motion-fast: ${tokens.motion.fast};
    --motion-base: ${tokens.motion.base};
    --motion-slow: ${tokens.motion.slow};

    --z-base: ${tokens.zIndex.base};
    --z-overlay: ${tokens.zIndex.overlay};
    --z-popover: ${tokens.zIndex.popover};

    --opacity-disabled: ${tokens.opacity.disabled};
    --opacity-muted: ${tokens.opacity.muted};
  }
`;

/**
 * Injects TOKENS_CSS into <head> as a <style> tag. Idempotent — safe to
 * call from multiple entry points. Use this from popup/sidebar main.tsx so
 * project CSS files can reference `var(--color-...)` everywhere.
 */
export function injectTokensIntoHead(): void {
  const id = '__design_review_tokens__';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = TOKENS_CSS;
  document.head.appendChild(style);
}

export type Tokens = typeof tokens;
